import React, { useEffect, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { CalendarDays, Plus, MapPin, Loader2, UserRound, Search } from "lucide-react";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { toast } from "sonner";

const Agenda = () => {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ titolo: "", tipo: "Appuntamento", data_inizio: "", luogo: "", descrizione: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/appointments"); setItems(data); } catch (e) { console.error("Errore caricamento agenda", e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.titolo.trim() || !f.data_inizio) { toast.error("Inserisci titolo e data/ora"); return; }
    setSaving(true);
    try {
      await api.post("/appointments", { ...f, data_inizio: new Date(f.data_inizio).toISOString() });
      toast.success("Appuntamento creato"); setOpen(false);
      setF({ titolo: "", tipo: "Appuntamento", data_inizio: "", luogo: "", descrizione: "" }); load();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  // Ricerca per nominativo, data, location (luogo) o operatore assegnato
  const filteredItems = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((a) =>
      (a.titolo || "").toLowerCase().includes(s) ||
      (a.persona_nome || "").toLowerCase().includes(s) ||
      (a.luogo || "").toLowerCase().includes(s) ||
      (a.operatore_nome || "").toLowerCase().includes(s) ||
      (a.data_inizio || "").slice(0, 10).includes(s) ||
      new Date(a.data_inizio || 0).toLocaleDateString("it-IT").includes(s));
  }, [items, q]);

  const groups = filteredItems.reduce((acc, a) => {
    const day = (a.data_inizio || "").slice(0, 10);
    (acc[day] = acc[day] || []).push(a); return acc;
  }, {});

  return (
    <div>
      <PageHeader icon={CalendarDays} title="Agenda" description="Appuntamenti, colloqui, riunioni ed eventi"
        actions={can("appointments.write") && <Button onClick={() => setOpen(true)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"><Plus className="h-4 w-4 mr-2" /> Nuovo appuntamento</Button>} />
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nominativo, data, luogo o operatore…" className="pl-9 pr-11" data-testid="agenda-search" />
        <div className="absolute right-2 top-1/2 -translate-y-1/2"><VoiceSearchButton onResult={(t) => setQ((p) => appendSearch(p, t))} data-testid="agenda-search-mic" /></div>
      </div>
      {loading ? <LoadingState /> : filteredItems.length === 0 ? <EmptyState title="Nessun appuntamento in agenda" /> : (
        <div className="space-y-5">
          {Object.entries(groups).sort().map(([day, list]) => (
            <div key={day}>
              <h3 className="font-display font-semibold text-sm text-slate-500 mb-2">{new Date(day).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</h3>
              <Card className="divide-y">
                {list.map((a) => (
                  <div key={a.id} className="p-3 flex items-start gap-3">
                    <div className="text-center shrink-0 w-14"><div className="font-display font-bold text-[color:var(--brand-blue-700)]">{(a.data_inizio || "").slice(11, 16)}</div></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{a.titolo}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 items-center">
                        <span>{a.tipo}</span>{a.persona_nome && <span className="font-medium text-slate-700">{a.persona_nome}</span>}{a.luogo && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.luogo}</span>}
                        {a.operatore_nome && <span className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand-green-700)]" data-testid="agenda-gestore"><UserRound className="h-3 w-3" /> {a.operatore_nome}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Nuovo appuntamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo *</Label><Input value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo</Label>
                <Select value={f.tipo} onValueChange={(v) => setF({ ...f, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Appuntamento", "Colloquio", "Consulenza", "Riunione", "Corso", "Evento", "Trasporto", "Assistenza sanitaria", "Scadenza"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Data e ora *</Label><Input type="datetime-local" value={f.data_inizio} onChange={(e) => setF({ ...f, data_inizio: e.target.value })} /></div>
            </div>
            <div><Label>Luogo</Label><Input value={f.luogo} onChange={(e) => setF({ ...f, luogo: e.target.value })} /></div>
            <div><Label>Note</Label><Textarea value={f.descrizione} onChange={(e) => setF({ ...f, descrizione: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agenda;
